import { createAction, createListenerMiddleware, createReducer, createSlice } from "@reduxjs/toolkit";
import { saveDraft, updateDraft, removeDraft, clearDrafts, duplicateDraft, createDraft, createDraftFromTemplateConfig } from "./actions";
import { nanoid } from "nanoid";
import { Project } from "@/shared/sync/dataTypes/Project";

// 1) New action that the listener will dispatch after rehydrate
export const resetAllIsAcquiringData = createAction('drafts/resetAllIsAcquiringData');
export interface DraftsState {
    projects: Project[];
}


const initialState: DraftsState = {
    projects: [],
};

// 3) Reducer with the new reset case
const draftsReducer = createReducer(initialState, (builder) => {
  builder
    // --- NEW: reset all flags after rehydrate
    .addCase(resetAllIsAcquiringData, (state) => {
      // immer will create new objects/array as needed
      state.projects.forEach(p => {
        // if nested, do: p.templateConfig.isAcquiringData = false
        if (p.isAcquiringData) {
          p.isDraftMode = true;
        }
        p.isAcquiringData = false;
      });
    })

    // --- your existing cases (kept as-is)
    .addCase(saveDraft, (state, action) => {
      const draft = action.payload as Project;
      const existingIndex = state.projects.findIndex((p) => p.projectId === draft.projectId);

      if (existingIndex === -1) {
        // ✅ Return a new state object with a new projects array
        return {
          ...state,
          projects: [...state.projects, draft], // Use spread syntax to create a new array
        };
      } else {
        // ✅ Also handle updates immutably
        return {
          ...state,
          projects: state.projects.map((project) =>
            project.projectId === draft.projectId ? draft : project
          ),
        };
      }
    })

    .addCase(updateDraft, (state, action) => {
      const draft = action.payload as Project;
      const existingIndex = state.projects.findIndex((p) => p.projectId === draft.projectId);
      

      if (existingIndex === -1) {
        // ✅ Return a new state object with a new projects array
        return {
          ...state,
          projects: [...state.projects, draft], // Use spread syntax to create a new array
        };
      } else {
        // ✅ Also handle updates immutably
        return {
          ...state,
          projects: state.projects.map((project) =>
            project.projectId === draft.projectId ? draft : project
          ),
        };
      }
    })

    .addCase(removeDraft, (state, action) => {
      state.projects = state.projects.filter((p) => p.projectId !== action.payload);
    })

    .addCase(clearDrafts, (state) => {
      state.projects = [];
    })

    .addCase(duplicateDraft, (state, action) => {
      const originalDraft = action.payload as Project;
      const newProjectId = nanoid();

      const newDraft = new Project(newProjectId);
      newDraft.templateConfig = { ...originalDraft.templateConfig, draftGuid: newProjectId };

      // ensure the acquiring flag is reset on duplicates (optional)
      // if top-level:
      if ('isAcquiringData' in newDraft) {
        (newDraft as any).isAcquiringData = false;
      }
      // if nested:
      // newDraft.templateConfig.isAcquiringData = false;

      state.projects.push(newDraft);
    });
});

export default draftsReducer;
